import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart2, Zap, MessageSquare, BookOpen, FileText,
    Loader2, AlertTriangle, Layers, Award,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import { SectionHeader, Section } from './OverviewTabV2';

/**
 * FeatureUsageTabV2 — "Which features are earning real usage?"
 */

const FeatureUsageTabV2 = ({ filters = {} }) => {
    const [featureUsage, setFeatureUsage] = useState(null);
    const [workflow, setWorkflow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };

            const [fuRes, wfRes] = await Promise.allSettled([
                adminAnalytics.getFeatureUsage(params),
                adminAnalytics.getWorkflowAnalytics(params),
            ]);

            if (fuRes.status === 'fulfilled') setFeatureUsage(fuRes.value.data);
            if (wfRes.status === 'fulfilled') setWorkflow(wfRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load feature usage data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading feature usage…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    const workflows = workflow?.workflows || [];
    const topWorkflow = workflows.length > 0 ? workflows.reduce((a, b) => (a.volume || 0) > (b.volume || 0) ? a : b) : null;
    const topEvents = featureUsage?.top_events || [];
    const byCategory = featureUsage?.by_category || [];
    const byFeatureArea = featureUsage?.by_feature_area || [];

    // Extract workflow volumes by known product area
    const getWorkflowVolume = (name) => {
        const wf = workflows.find(w => w.workflow === name);
        return wf?.volume ?? 0;
    };

    return (
        <div>
            <SectionHeader
                title="Feature Usage"
                subtitle="Which features are earning real usage?"
            />

            {/* ── Workflow Volumes ────────────────────────────────── */}
            <Section title="Workflow Volume" icon={Layers} color="#6366f1">
                {topWorkflow && (
                    <MetricCard
                        label="Top Workflow"
                        value={topWorkflow.workflow?.replace(/_/g, ' ') || '—'}
                        icon={Award} accent="#f59e0b"
                        sub={`${topWorkflow.volume ?? 0} events`}
                    />
                )}
                <MetricCard label="Lesson Workflow" value={getWorkflowVolume('lesson_generation')} icon={Zap} accent="#10b981" />
                <MetricCard label="Chat Workflow" value={getWorkflowVolume('chat_assist')} icon={MessageSquare} accent="#3b82f6" />
                <MetricCard label="Grading Workflow" value={getWorkflowVolume('grading')} icon={BookOpen} accent="#8b5cf6" />
                <MetricCard label="Deliverables" value={getWorkflowVolume('deliverables')} icon={FileText} accent="#f59e0b" />
                <MetricCard label="Campus Intelligence" value={getWorkflowVolume('campus_intelligence')} icon={BarChart2} accent="#ec4899" />
            </Section>

            {/* ── Top Events ─────────────────────────────────────── */}
            {topEvents.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        marginBottom: 12, paddingBottom: 8,
                        borderBottom: '2px solid #10b98120',
                    }}>
                        <Zap size={15} style={{ color: '#10b981' }} />
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Top Events</h3>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{topEvents.length} events</span>
                    </div>
                    <div style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            display: 'grid', gridTemplateColumns: '1fr 100px 120px',
                            padding: '10px 16px', background: '#f8fafc',
                            borderBottom: '1px solid #e5e7eb', gap: 8,
                        }}>
                            {['Event', 'Count', 'Category'].map(h => (
                                <span key={h} style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{h}</span>
                            ))}
                        </div>
                        {topEvents.slice(0, 15).map((ev, i) => (
                            <div key={ev.event_name || i} style={{
                                display: 'grid', gridTemplateColumns: '1fr 100px 120px',
                                padding: '10px 16px', gap: 8,
                                borderBottom: i < Math.min(topEvents.length, 15) - 1 ? '1px solid #f1f5f9' : 'none',
                            }}>
                                <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>{ev.event_name}</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{ev.count}</span>
                                <span style={{
                                    fontSize: 10, fontWeight: 600, padding: '2px 6px',
                                    borderRadius: 4, background: '#f0f4f8', color: '#475569',
                                    display: 'inline-block', width: 'fit-content',
                                }}>
                                    {ev.event_category || '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── By Category ────────────────────────────────────── */}
            {byCategory.length > 0 && (
                <BreakdownSection title="By Category" data={byCategory} color="#8b5cf6" />
            )}

            {/* ── By Feature Area ────────────────────────────────── */}
            {byFeatureArea.length > 0 && (
                <BreakdownSection title="By Feature Area" data={byFeatureArea} color="#3b82f6" />
            )}
        </div>
    );
};


const BreakdownSection = ({ title, data, color }) => {
    const maxCount = Math.max(...data.map(d => d.count || 0), 1);

    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 8,
                borderBottom: `2px solid ${color}20`,
            }}>
                <BarChart2 size={15} style={{ color }} />
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 20px' }}>
                {data.map((d, idx) => {
                    const count = d.count || 0;
                    const barWidth = maxCount > 0 ? Math.max((count / maxCount) * 100, count > 0 ? 2 : 0) : 0;
                    return (
                        <div key={d.category || d.feature_area || idx} style={{ marginBottom: idx < data.length - 1 ? 10 : 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                                    {d.category || d.feature_area || d.name || '—'}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{count}</span>
                            </div>
                            <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', width: `${barWidth}%`,
                                    background: color, borderRadius: 3,
                                    transition: 'width 0.4s', opacity: 0.7,
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


export default FeatureUsageTabV2;
